import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Button,
  Table,
  TableBody,
  TableBodyData,
  TableBodyDataCellTemplate,
  TableBodyDataRowTemplate,
  TableFilterTextInput,
  TableFilterNone,
  TableFilters,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TablePagination,
  TableSortingRule,
  TableSortingRules,
  TableWrapper,
} from "@utahdts/utah-design-system";
import {
  GET_ALL_SECTION_CONDITIONS,
} from "../../queries/siteVisitQrys/sectionConditionsQrys";
import SectionConditionModal from "./SectionConditionModal";
import "./siteVisits.css";

const ManageSectionConditions = () => {
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };

  const canView = user?.permissions?.includes("view_survey_question");
  const canEdit = user?.permissions?.includes("edit_survey_question");

  const { data, loading, error, refetch } = useQuery(GET_ALL_SECTION_CONDITIONS);
  const conditions = (data?.getAllSectionConditions ?? []).map((condition) => ({
    ...condition,
    showGroupDescriptionLabel: String(condition.showGroupDescription),
  }));
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedConditionId, setSelectedConditionId] = useState(null);

  const handleOpenNew = () => {
    setSelectedConditionId(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (condition) => {
    setSelectedConditionId(condition.conditionId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedConditionId(null);
  };

  if (error || !canView) {
    return (
      <div className="utah-design-system page-container">
        <h1>Manage Section Conditions</h1>
        <p>{!canView ? "You do not have permissions to view this page" : error?.message}</p>
      </div>
    );
  }

  return (
    <div className="utah-design-system page-container">
      <h1>Manage Section Conditions</h1>

      {canEdit && (
        <Button
          appearance="solid"
          variant="primary"
          className="mb-spacing mt-spacing"
          onClick={handleOpenNew}
        >
          New Condition
        </Button>
      )}

      {loading && <p>Loading...</p>}

      {!loading && conditions.length > 0 && (
        <TableWrapper>
          <div
            className={`site-visits-show-entries ${!canEdit ? "site-visits-show-entries--no-edit" : ""}`.trim()}
          >
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="site-visits-show-entries__select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          <Table className="table--alt">
            <TableHead>
              <TableSortingRules>
                <TableSortingRule allyLabel="Sort by Id" recordFieldPath="conditionId" fieldType="number" />
                <TableSortingRule allyLabel="Sort by Description" recordFieldPath="description" />
                <TableSortingRule allyLabel="Sort by Show Group" recordFieldPath="showGroupDescriptionLabel" />
                <TableSortingRule allyLabel="Sort by Status" recordFieldPath="status" />
              </TableSortingRules>
              <TableHeadRow>
                <TableHeadCell recordFieldPath="conditionId">Id</TableHeadCell>
                <TableHeadCell recordFieldPath="description">Description</TableHeadCell>
                <TableHeadCell recordFieldPath="showGroupDescriptionLabel">Show Group</TableHeadCell>
                <TableHeadCell recordFieldPath="status">Status</TableHeadCell>
                <TableHeadCell recordFieldPath="actions"></TableHeadCell>
              </TableHeadRow>
              <TableFilters>
                <TableFilterTextInput
                  a11yLabel="Filter by Id"
                  recordFieldPath="conditionId"
                  placeholder="filter"
                />
                <TableFilterTextInput
                  a11yLabel="Filter by Description"
                  recordFieldPath="description"
                  placeholder="filter"
                />
                <TableFilterTextInput
                  a11yLabel="Filter by Show Group"
                  recordFieldPath="showGroupDescriptionLabel"
                  placeholder="filter"
                />
                <TableFilterTextInput
                  a11yLabel="Filter by Status"
                  recordFieldPath="status"
                  placeholder="filter"
                />
                <TableFilterNone aria-label="Actions">
                  <span className="visually-hidden">Actions</span>
                </TableFilterNone>
              </TableFilters>
            </TableHead>
            <TableBody>
              <TableBodyData records={conditions} recordIdField="conditionId">
                <TableBodyDataRowTemplate>
                  <TableBodyDataCellTemplate recordFieldPath="conditionId" />
                  <TableBodyDataCellTemplate recordFieldPath="description" />
                  <TableBodyDataCellTemplate recordFieldPath="showGroupDescriptionLabel" />
                  <TableBodyDataCellTemplate recordFieldPath="status" />
                  <TableBodyDataCellTemplate>
                    {({ record }) => (
                      <button
                        className="link-button"
                        onClick={() => handleOpenEdit(record)}
                      >
                        {canEdit ? "Edit" : "View"}
                      </button>
                    )}
                  </TableBodyDataCellTemplate>
                </TableBodyDataRowTemplate>
              </TableBodyData>
            </TableBody>
          </Table>
          {conditions.length > itemsPerPage && (
            <TablePagination
              className="mt-spacing"
              id="manage-section-conditions-pagination"
              itemsPerPage={itemsPerPage}
            />
          )}
        </TableWrapper>
      )}

      {modalOpen && (
        <SectionConditionModal
          className="wl-modal"
          onClose={handleCloseModal}
          selectedConditionId={selectedConditionId}
          canEdit={canEdit}
          refetch={refetch}
        />
      )}
    </div>
  );
};

export default ManageSectionConditions;
